import { Toggle as BaseToggle } from '@base-ui/react/toggle'
import { ToggleGroup as BaseToggleGroup } from '@base-ui/react/toggle-group'
import { createContext, useCallback, useContext, useState } from 'react'

import { cn } from '#lib/cn'

import { toggleGroupVariants, toggleVariants } from './styles'
import {
  type ToggleGroupMultipleProps,
  type ToggleGroupProps,
  type ToggleGroupSingleProps,
  type ToggleProps,
  ToggleSize,
  type ToggleSize as ToggleSizeType,
  type ToggleVariant,
} from './types'

/**
 * Context for sharing state between ToggleGroup and Toggle
 */
const ToggleGroupContext = createContext<{
  size: ToggleSizeType | undefined
  variant: ToggleVariant
  inGroup: boolean
}>({ size: undefined, variant: 'default', inGroup: false })

/**
 * Container component that provides shared state to toggle buttons.
 *
 * Discriminated by the `type` prop (defaults to `'single'`):
 * - `type="single"` (default): exactly one item is selected at a time. The
 *   active toggle cannot be deselected, so `value` is always a populated string.
 * - `type="multiple"`: zero or more items can be selected. Empty selection is
 *   a valid state.
 *
 * @example
 * ```tsx
 * // Single-select (default)
 * <ToggleGroup defaultValue="grid" size={ToggleSize.Medium}>
 *   <Toggle value="grid">Grid</Toggle>
 *   <Toggle value="list">List</Toggle>
 * </ToggleGroup>
 *
 * // Multi-select
 * <ToggleGroup type="multiple" defaultValue={['bold']}>
 *   <Toggle value="bold">Bold</Toggle>
 *   <Toggle value="italic">Italic</Toggle>
 * </ToggleGroup>
 * ```
 */
export function ToggleGroup(props: ToggleGroupProps) {
  if (props.type === 'multiple') {
    const { type: _type, ...rest } = props
    return <MultipleToggleGroup {...rest} />
  }
  const { type: _type, ...rest } = props
  return <SingleToggleGroup {...rest} />
}

ToggleGroup.displayName = 'ToggleGroup'

function SingleToggleGroup({
  className,
  size,
  variant = 'default',
  itemWidth,
  children,
  ref,
  value: valueProp,
  onValueChange,
  defaultValue,
  ...props
}: Omit<ToggleGroupSingleProps, 'type'>) {
  const isControlled = valueProp !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? '')
  const resolvedValue = isControlled ? valueProp : internalValue
  const baseValue = resolvedValue ? [resolvedValue] : []

  const handleValueChange = (newValue: string[]) => {
    // Always exactly one selected: ignore deselect-to-empty.
    if (newValue.length === 0) {
      return
    }
    const nextValue = newValue[0] ?? ''
    if (!isControlled) {
      setInternalValue(nextValue)
    }
    onValueChange?.(nextValue)
  }

  return (
    <ToggleGroupContext.Provider value={{ size, variant, inGroup: true }}>
      <BaseToggleGroup
        className={cn(toggleGroupVariants({ variant, itemWidth }), className)}
        multiple={false}
        onValueChange={handleValueChange}
        ref={ref}
        value={baseValue}
        {...props}
      >
        {children}
      </BaseToggleGroup>
    </ToggleGroupContext.Provider>
  )
}

function MultipleToggleGroup({
  className,
  size,
  variant = 'default',
  itemWidth,
  children,
  ref,
  value: valueProp,
  onValueChange,
  defaultValue,
  ...props
}: Omit<ToggleGroupMultipleProps, 'type'>) {
  const isControlled = valueProp !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? [])
  const resolvedValue = isControlled ? valueProp : internalValue

  const handleValueChange = (newValue: string[]) => {
    if (!isControlled) {
      setInternalValue(newValue)
    }
    onValueChange?.(newValue)
  }

  return (
    <ToggleGroupContext.Provider value={{ size, variant, inGroup: true }}>
      <BaseToggleGroup
        className={cn(toggleGroupVariants({ variant, itemWidth }), className)}
        multiple={true}
        onValueChange={handleValueChange}
        ref={ref}
        value={resolvedValue}
        {...props}
      >
        {children}
      </BaseToggleGroup>
    </ToggleGroupContext.Provider>
  )
}

/**
 * Toggle button that works both standalone and within a ToggleGroup.
 * When standalone, SVG-only content automatically renders as square.
 * When in a group, the grid handles equal sizing.
 *
 * `variant` resolves from the explicit prop, then the group context, defaulting
 * to `default`. Chips auto-width to their content; `size` controls only height.
 *
 * Chips need an `aria-label` - the visible letter is not a sufficient accessible
 * name. This is type-enforced only when `variant="chip"` is set directly on the
 * Toggle; when the variant is inherited from a chip `ToggleGroup` (the common
 * case) TypeScript cannot see it, so pass `aria-label` on every chip.
 *
 * @example
 * ```tsx
 * // Standalone
 * <Toggle aria-label="Bold">
 *   <BoldIcon />
 * </Toggle>
 *
 * // Within a group
 * <ToggleGroup defaultValue="grid">
 *   <Toggle value="grid">Grid</Toggle>
 *   <Toggle value="list">List</Toggle>
 * </ToggleGroup>
 *
 * // Chip variant (letter content, aria-label required)
 * <ToggleGroup type="multiple" variant="chip">
 *   <Toggle value="discretionary" aria-label="Discretionary">D</Toggle>
 *   <Toggle value="settled" aria-label="Settled">S</Toggle>
 * </ToggleGroup>
 * ```
 */
// Shows the full label as a native tooltip on hover, only when it is truncated.
function useTruncationTitle(text: string | undefined) {
  return useCallback(
    (el: HTMLSpanElement | null) => {
      if (!el || text === undefined) {
        return
      }
      const onEnter = () => {
        if (el.scrollWidth > el.clientWidth) {
          el.title = text
        } else {
          el.removeAttribute('title')
        }
      }
      el.addEventListener('mouseenter', onEnter)
      return () => el.removeEventListener('mouseenter', onEnter)
    },
    [text],
  )
}

export function Toggle(props: ToggleProps) {
  const {
    size: contextSize,
    variant: contextVariant,
    inGroup,
  } = useContext(ToggleGroupContext)

  const {
    variant: variantProp,
    size: sizeProp,
    className,
    children,
    ref,
    ...rest
  } = props
  const variant = variantProp ?? contextVariant
  const resolvedSize = sizeProp ?? contextSize
  // Chips default to `xs`; the default variant falls back to the CVA's Medium
  const size =
    resolvedSize ?? (variant === 'chip' ? ToggleSize.XSmall : undefined)
  const textLabel =
    variant !== 'chip' && typeof children === 'string' ? children : undefined
  // A caller-supplied title (e.g. why an option is disabled) takes precedence
  const labelRef = useTruncationTitle(
    rest.title === undefined ? textLabel : undefined,
  )

  return (
    <BaseToggle
      className={cn(toggleVariants({ variant, size, inGroup }), className)}
      ref={ref}
      {...rest}
    >
      {textLabel === undefined ? (
        children
      ) : (
        <span className="truncate" ref={labelRef}>
          {textLabel}
        </span>
      )}
    </BaseToggle>
  )
}

Toggle.displayName = 'Toggle'
